import React from "react";
import Button from "../../../shared/CustomButton";
import { Formik } from "formik";
import * as Yup from "yup";
import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import DatePicker from "../../../shared/DatePicker";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt } from "react-icons/fa";
import CheckPermission from "../../../hooks/CheckPermission";
export default function AddWriteOffList({ onClose, onSubmit }) {
  const { hasPermission } = CheckPermission();
  const { t } = useTranslation();
  const initialValues = {
    itemName: "",
    quantity: "",
    totalAmount: "",
    rate: "",
    writeOffDate: "",
    warehouse: "",
    approver: "",
    writeOffType: "",
    reason: "",
    remarks: "",
  };
  const validationSchema = Yup.object().shape({
    itemName: Yup.string().required(t("Item Name is required")),
    quantity: Yup.number()
      .typeError(t("Quantity must be a number"))
      .required(t("Quantity is required")),
    totalAmount: Yup.number()
      .typeError(t("Total Amount must be a number"))
      .required(t("Total Amount is required")),
    writeOffDate: Yup.date().required(t("Write-Off Date is required")),
    rate: Yup.number()
      .typeError(t("Rate must be a number"))
      .required(t("Rate is required")),
    warehouse: Yup.string().required(t("Warehouse is required")),
    approver: Yup.string().required(t("Approver is required")),
    writeOffType: Yup.string().required(t("Write-off Type is required")),
    reason: Yup.string().required(t("Reason is required")),
    remarks: Yup.string().required(t("Remarks are required")),
  });
  const handleSubmit = (values, { resetForm }) => {
    if (!hasPermission('inventory', 'create')) return;
    onSubmit(values);
    resetForm();
    onClose();
  };
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        {/* <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-blue-500 pb-2">
          {t("Add Write Off")}
        </h2> */}
        <p className="text-gray-600 text-sm mt-2">
          {t("Fill in the details to create a new write-off record")}
        </p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
                  1
                </span>
                {t("Basic Information")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextInput name="itemName" label={t("Item Name")} />
                <TextInput
                  name="quantity"
                  label={t("Quantity")}
                  type="number"
                  step="any"
                />
                <div className="relative">
                  <DatePicker
                    name="writeOffDate"
                    label={t("Write-Off Date")}
                    dateFormat="dd/mm/yy"
                    placeholder={t("Select date (DD/MM/YYYY)")}
                    showIcon={false}
                    className="w-full pr-10"
                    readOnlyInput={true}
                    inputStyle={{ cursor: "default" }}
                  />
                  <div
                    className="absolute right-3 top-9 cursor-pointer z-10 p-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      const datePickerInput = e.target
                        .closest(".relative")
                        .querySelector("input");
                      if (datePickerInput) {
                        datePickerInput.click();
                        datePickerInput.focus();
                      }
                    }}
                  >
                    <FaCalendarAlt className="text-blue-500 hover:text-blue-700 w-4 h-4" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <TextInput
                    name="rate"
                    label={t("Rate")}
                    type="number"
                    step="any"
                  />
                  <TextInput
                    name="totalAmount"
                    label={t("Total Amount")}
                    type="number"
                    step="any"
                  />
                </div>
              </div>
            </div>

            {/* Location & Approval Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
                  2
                </span>
                {t("Location & Approval")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DropdownField
                  name="warehouse"
                  label={t("Warehouse / Location")}
                  options={[
                    { value: "", label: t("Select Warehouse") },
                    { value: "Main warehouse", label: "Main Warehouse" },
                    { value: "Pharmacy store", label: "Pharmacy Store" },
                    { value: "Lab storage", label: "Lab Storage" },
                    { value: "Emergency stock", label: "Emergency Stock" },
                  ]}
                  optionValue="value"
                  optionLabel="label"
                />
                <DropdownField
                  name="approver"
                  label={t("Responsible Person / Approver")}
                  options={[
                    { value: "", label: t("Select Approver") },
                    { value: "Inventory manager", label: "Inventory Manager" },
                    { value: "Department head", label: "Department Head" },
                    { value: "Supervisor", label: "Supervisor" },
                    { value: "Admin", label: "Administrator" },
                  ]}
                  optionValue="value"
                  optionLabel="label"
                />
              </div>
            </div>

            {/* Write-off Details Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                <span className="bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">
                  3
                </span>
                {t("Write-off Details")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DropdownField
                  name="writeOffType"
                  label={t("Write-off Type")}
                  options={[
                    { value: "full", label: "Full Write-off" },
                    { value: "partial", label: "Partial Write-off" },
                  ]}
                  optionValue="value"
                  optionLabel="label"
                />
                <DropdownField
                  name="reason"
                  label={t("Reason")}
                  options={[
                    { value: "damage", label: "Damage" },
                    { value: "expired", label: "Expired" },
                    { value: "lost", label: "Lost" },
                    { value: "obsolete", label: "Obsolete" },
                  ]}
                  optionValue="value"
                  optionLabel="label"
                />
              </div>
              <div className="mt-4">
                <TextInput name="remarks" label={t("Remarks")} rows={3} />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <Button
                type="button"
                onClick={onClose}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg"
              >
                {t("Cancel")}
              </Button>
              {hasPermission('inventory', 'create') && (
              <Button
                type="submit"
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-2 rounded-lg font-semibold"
              >
                {t("Submit Write-off")}
              </Button>
              )}
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}
